import { useEffect } from "react"

/**
 * Client-side <head> management without react-helmet.
 *
 * react-helmet relies on the deprecated UNSAFE_componentWillMount lifecycle
 * (via react-side-effect), which warns under React 19 StrictMode. These routes
 * are `ssr: false`, so head tags are only ever applied on the client anyway —
 * a small useEffect-based hook does the same job with no dependency.
 */

export type HeadTag = {
  tag: `meta` | `link` | `script`
  attrs: Record<string, string>
  /** inner text content, e.g. JSON-LD for a <script> tag */
  text?: string
}

/** Sets document.title while mounted, restoring the previous title on unmount. */
export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    if (!title || typeof document === `undefined`) return
    const previous = document.title
    document.title = title
    return () => {
      document.title = previous
    }
  }, [title])
}

function selectorFor(t: HeadTag): string | null {
  if (t.tag === `meta`) {
    if (t.attrs.name) return `meta[name="${t.attrs.name}"]`
    if (t.attrs.property) return `meta[property="${t.attrs.property}"]`
  }
  if (t.tag === `link` && t.attrs.rel) return `link[rel="${t.attrs.rel}"]`
  return null // scripts (and unkeyed tags) are always created fresh
}

/**
 * Injects the given tags into <head> while mounted. Existing matching tags
 * (by meta name/property or link rel) are updated in place and restored on
 * unmount; everything else is appended and removed on unmount.
 *
 * Pass a stable `key` (e.g. JSON.stringify of the inputs) so the effect only
 * re-runs when the resolved tags actually change.
 */
export function useHeadTags(tags: HeadTag[], key: string) {
  useEffect(() => {
    if (typeof document === `undefined`) return
    const restores: Array<() => void> = []

    for (const t of tags) {
      const selector = selectorFor(t)
      const existing = selector
        ? document.head.querySelector(selector)
        : null

      if (existing) {
        const original = existing.cloneNode(true) as Element
        for (const [k, v] of Object.entries(t.attrs)) existing.setAttribute(k, v)
        if (t.text !== undefined) existing.textContent = t.text
        restores.push(() => existing.replaceWith(original))
      } else {
        const el = document.createElement(t.tag)
        for (const [k, v] of Object.entries(t.attrs)) el.setAttribute(k, v)
        if (t.text !== undefined) el.textContent = t.text
        document.head.appendChild(el)
        restores.push(() => el.remove())
      }
    }

    return () => {
      for (let i = restores.length - 1; i >= 0; i--) restores[i]()
    }
     
  }, [key])
}
